import React, { useState } from 'react';
import { ChevronRightIcon, CrossIcon, SearchIcon } from '../../../assets/icons/icons';
import { PERIOD_OPTIONS, STATUS_META } from '../../../data/projectsTable';
import {
    isDateRange,
    type PeriodFilter,
    type StatusCounts,
    type StatusFilter,
} from '../../../types/projectsTable';
import { cx } from '../../../utils/cx';
import { DateRangeCalendar, formatDateRangeLabel } from '../../ui/DatePicker';
import { FilterMenu } from './FilterMenu';
import styles from './ProjectsToolbar.module.scss';

const STATUS_ORDER: StatusFilter[] = ['all', 'new', 'run', 'done'];

const periodLabel = (period: PeriodFilter): string => {
    if (isDateRange(period)) return formatDateRangeLabel(period);
    return PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? 'Весь период';
};

interface PeriodBodyProps {
    period: PeriodFilter;
    onChange: (value: PeriodFilter) => void;
    close: () => void;
}

function PeriodBody({ period, onChange, close }: PeriodBodyProps) {
    const [calendar, setCalendar] = useState(false);

    if (calendar) {
        return (
            <DateRangeCalendar
                value={isDateRange(period) ? period : null}
                months={1}
                onChange={(range) => {
                    if (range) {
                        onChange(range);
                        close();
                    } else {
                        setCalendar(false);
                    }
                }}
            />
        );
    }

    return (
        <div className={styles.list}>
            {PERIOD_OPTIONS.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    className={cx(styles.option, !isDateRange(period) && period === option.value && styles.on)}
                    onClick={() => {
                        onChange(option.value);
                        close();
                    }}
                >
                    {option.label}
                </button>
            ))}

            <div className={styles.sep} />

            <button
                type="button"
                className={cx(styles.option, styles.custom, isDateRange(period) && styles.on)}
                onClick={() => setCalendar(true)}
            >
                Произвольный период
                <ChevronRightIcon className={styles.go} />
            </button>
        </div>
    );
}

interface ProjectsToolbarProps {
    query: string;
    status: StatusFilter;
    period: PeriodFilter;
    author: string;
    authors: string[];
    counts: StatusCounts;
    isFiltered: boolean;
    onQueryChange: (value: string) => void;
    onStatusChange: (value: StatusFilter) => void;
    onPeriodChange: (value: PeriodFilter) => void;
    onAuthorChange: (value: string) => void;
    onReset: () => void;
}

export function ProjectsToolbar({
    query,
    status,
    period,
    author,
    authors,
    counts,
    isFiltered,
    onQueryChange,
    onStatusChange,
    onPeriodChange,
    onAuthorChange,
    onReset,
}: ProjectsToolbarProps) {
    return (
        <section className={styles.toolbar}>
            <div className={styles.search}>
                <SearchIcon className={styles.searchIcon} />
                <input
                    value={query}
                    onChange={(event) => onQueryChange(event.target.value)}
                    placeholder="Поиск по названию, заказчику или номеру позиции"
                    autoComplete="off"
                    aria-label="Поиск по проектам"
                />
                {query && (
                    <button
                        type="button"
                        className={styles.clear}
                        onClick={() => onQueryChange('')}
                        aria-label="Очистить поиск"
                    >
                        <CrossIcon />
                    </button>
                )}
            </div>

            <div className={styles.filters}>
                <button
                    type="button"
                    className={styles.reset}
                    onClick={onReset}
                    disabled={!isFiltered}
                >
                    Сбросить
                </button>

                <FilterMenu
                    label={status === 'all' ? 'Все статусы' : STATUS_META[status].label}
                    active={status !== 'all'}
                >
                    {(close) => (
                        <div className={styles.list}>
                            {STATUS_ORDER.map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={cx(styles.option, status === value && styles.on)}
                                    onClick={() => {
                                        onStatusChange(value);
                                        close();
                                    }}
                                >
                                    <i className={cx(styles.dot, styles[value])} />
                                    {value === 'all' ? 'Все статусы' : STATUS_META[value].label}
                                    <span className={styles.count}>{counts[value]}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </FilterMenu>

                <FilterMenu label={periodLabel(period)} active={period !== 'all'} wide>
                    {(close) => <PeriodBody period={period} onChange={onPeriodChange} close={close} />}
                </FilterMenu>

                <FilterMenu label={author === 'all' ? 'Все заказчики' : author} active={author !== 'all'}>
                    {(close) => (
                        <div className={styles.list}>
                            <button
                                type="button"
                                className={cx(styles.option, author === 'all' && styles.on)}
                                onClick={() => {
                                    onAuthorChange('all');
                                    close();
                                }}
                            >
                                Все заказчики
                            </button>

                            {authors.map((name) => (
                                <button
                                    key={name}
                                    type="button"
                                    className={cx(styles.option, author === name && styles.on)}
                                    onClick={() => {
                                        onAuthorChange(name);
                                        close();
                                    }}
                                >
                                    {name}
                                </button>
                            ))}
                        </div>
                    )}
                </FilterMenu>
            </div>
        </section>
    );
}
